import camelCase from 'lodash/camelCase';
import snakeCase from 'lodash/snakeCase';
import { isPlainObject, isString } from '@flowio/is';
import { SerializableObject } from '@flowio/web-sdk';
import type { CamelCasedPropertiesDeep, SnakeCasedPropertiesDeep } from 'type-fest';
import keys from './keys';

interface MutatorFunction {
  (str: string | number | symbol): string | number | symbol
}

export default function mapKeysNested<
C extends SerializableObject, S extends object,
>(
  obj: C,
  mutator: MutatorFunction,
): S {
  return keys(obj).reduce((acc, key) => {
    const origKey = key;
    const mutatedKey = mutator(origKey);
    const value = obj[origKey];
    if (isPlainObject(value)) {
      return { ...acc, [mutatedKey]: mapKeysNested(value, mutator) };
    }
    return { ...acc, [mutatedKey]: value };
  }, {} as S);
}

export function toCamelCaseKey(str: string | number | symbol): string | number | symbol {
  if (!isString(str)) {
    return str;
  }
  return camelCase(str);
}

export function toCamelCaseKeysNested<C extends {}>(
  obj: C,
): CamelCasedPropertiesDeep<C> {
  return mapKeysNested<C, CamelCasedPropertiesDeep<C>>(obj, toCamelCaseKey);
}

export function toSnakeCaseKey(str: string | number | symbol): string | number | symbol {
  if (!isString(str)) {
    return str;
  }
  return snakeCase(str);
}

export function toSnakeCaseKeysNested<
C extends {},
S extends object = SnakeCasedPropertiesDeep<C>,
>(
  obj: C,
): S {
  return mapKeysNested<C, S>(obj, toSnakeCaseKey);
}
